// Fuzzy "same person, different MRN" detection for the add-patient forms.
//
// Every add-patient flow already refuses an exact MRN collision. That check only
// catches a duplicate when the MRN was typed correctly. A mistyped MRN sails
// straight through and creates a second record for a patient who is already in
// the portal. This module is the second pass: given the name + DOB the user just
// typed, it finds existing patients whose DOB matches exactly and whose name is
// the same *person* even when the strings differ (nickname, missing middle
// name/initial, hyphenated surname, a one-character typo, "Last, First" order).
//
// Deliberately Firebase-free and side-effect-free so it can be unit tested with
// plain Jest and reused by any caller regardless of which backend the candidate
// list came from. Callers pass the candidates in; this module never reads them.
//
// It is advisory only. A match is a WARNING, never a block: real patients do
// share a name and a birthday, so the decision stays with the coordinator.

/* -------------------------------------------------------------------------- */
/* Normalization                                                              */
/* -------------------------------------------------------------------------- */

// Digits-only MRN, used solely to skip candidates the exact-MRN check already
// covers. Not padded, because this is an equality test between two raw inputs,
// not the 9-digit canonical form the coordinator page stores.
export const normalizeMrnDigits = (raw) => String(raw || "").replace(/\D/g, "");

/* -------------------------------------------------------------------------- */
/* The portal's one rule for "is this the same patient?" (decided 2026-10-07)   */
/* Every place that checks for an existing patient uses these: Add Patient     */
/* (Coordinator), Reception, Raw Database's Move, and PFT Uploads' Move.       */
/*   1. Same MRN: same digits, leading zeros ignored (12345 = 000012345).      */
/*   2. Same DOB and the same person's name (findPotentialDuplicates).          */
/*   3. Same DOB and an MRN ONE DIGIT off: one digit changed, or two           */
/*      neighbouring digits swapped (a misread scan or a typo), even when the  */
/*      name is written differently.                                           */
/* What each page DOES with a match (block, or warn and let staff decide) is   */
/* the page's own choice; what COUNTS as a possible match is this rule.        */
/* -------------------------------------------------------------------------- */

/** MRN digits with leading zeros dropped: the form MRNs are compared in. */
export const mrnKey = (raw) => normalizeMrnDigits(raw).replace(/^0+/, "");

/** Rule 1: the same MRN (leading zeros and separators ignored). Blank never matches. */
export const sameMrnNumber = (a, b) => {
  const x = mrnKey(a);
  return !!x && x === mrnKey(b);
};

/** Rule 3's MRN half: one changed digit, or one swap of neighbouring digits. */
export const mrnOneDigitApart = (a, b) => {
  const x = mrnKey(a);
  const y = mrnKey(b);
  if (!x || !y || x === y || x.length !== y.length) return false;
  const diff = [...x].map((ch, i) => (ch !== y[i] ? i : -1)).filter((i) => i >= 0);
  if (diff.length === 1) return true;
  return diff.length === 2 && diff[1] === diff[0] + 1 && x[diff[0]] === y[diff[1]] && x[diff[1]] === y[diff[0]];
};

/** Rule 1 over a candidate list: every record with the same MRN. */
export const findSameMrn = (mrn, candidates = []) => candidates.filter((c) => c && sameMrnNumber(c.mrn, mrn));

export const MRN_ONE_DIGIT_REASON = "MRN is one digit different from the one entered (a possible typo)";

// Suffixes and credentials that carry no identity and would otherwise be read as
// a surname ("John Smith Jr" vs "John Smith").
const NAME_SUFFIXES = new Set([
  "jr", "sr", "ii", "iii", "iv", "v",
  "md", "do", "phd", "rn", "np", "pa",
]);

// Common formal name <-> nickname groups. Each group is a set of forms that mean
// the same first name. This is intentionally a short, high-confidence list: a
// wrong entry here creates false duplicate warnings, and the DOB must already
// match exactly for any of it to be consulted.
const NICKNAME_GROUPS = [
  ["abigail", "abby", "abbie"],
  ["alexander", "alex", "xander", "sandy"],
  ["alexandra", "alex", "alexa", "sasha", "sandra"],
  ["andrew", "andy", "drew"],
  ["anthony", "tony"],
  ["barbara", "barb", "babs"],
  ["benjamin", "ben", "benny"],
  ["bernard", "bernie"],
  ["catherine", "katherine", "kathryn", "cathy", "kathy", "kate", "katie", "kat", "cat"],
  ["charles", "charlie", "chuck", "chas"],
  ["christopher", "chris", "topher"],
  ["christine", "christina", "chris", "chrissy", "tina"],
  ["daniel", "dan", "danny"],
  ["deborah", "debra", "deb", "debbie"],
  ["donald", "don", "donnie"],
  ["dorothy", "dot", "dottie", "dolly"],
  ["edward", "ed", "eddie", "ted", "teddy"],
  ["elizabeth", "liz", "lizzie", "beth", "betty", "betsy", "eliza", "libby"],
  ["frederick", "fred", "freddie"],
  ["gerald", "gerry", "jerry"],
  ["gregory", "greg"],
  ["james", "jim", "jimmy", "jamie"],
  ["jennifer", "jen", "jenny", "jenn"],
  ["jeffrey", "geoffrey", "jeff"],
  ["john", "jon", "johnny", "jack"],
  ["jonathan", "jon", "jonny"],
  ["joseph", "joe", "joey"],
  ["joshua", "josh"],
  ["katherine", "kathleen", "kathy", "kate"],
  ["kenneth", "ken", "kenny"],
  ["lawrence", "larry", "lars"],
  ["leonard", "leo", "lenny", "len"],
  ["margaret", "maggie", "meg", "peggy", "margie"],
  ["matthew", "matt"],
  ["michael", "mike", "mikey", "mick"],
  ["nicholas", "nick", "nicky"],
  ["patricia", "pat", "patty", "trish", "tricia"],
  ["patrick", "pat", "paddy"],
  ["peter", "pete"],
  ["philip", "phillip", "phil"],
  ["rebecca", "becca", "becky"],
  ["richard", "rick", "ricky", "dick", "rich", "richie"],
  ["robert", "rob", "bob", "bobby", "robbie"],
  ["ronald", "ron", "ronnie"],
  ["samuel", "sam", "sammy"],
  ["stephen", "steven", "steve", "stevie"],
  ["susan", "sue", "susie", "suzy"],
  ["theodore", "ted", "teddy", "theo"],
  ["thomas", "tom", "tommy"],
  ["timothy", "tim", "timmy"],
  ["virginia", "ginny", "ginger"],
  ["walter", "walt", "wally"],
  ["william", "will", "bill", "billy", "willie", "liam"],
  ["zachary", "zach", "zack"],
];

// token -> Set of group indexes. A token can sit in more than one group ("alex"
// is both Alexander and Alexandra, "pat" both Patricia and Patrick), so two
// tokens are nickname-equivalent when their group sets intersect.
const NICKNAME_INDEX = (() => {
  const index = new Map();
  NICKNAME_GROUPS.forEach((group, groupId) => {
    group.forEach((form) => {
      if (!index.has(form)) index.set(form, new Set());
      index.get(form).add(groupId);
    });
  });
  return index;
})();

// Strip diacritics so "José" and "Jose" are the same token. The combining-mark
// range is written with escapes so the source file stays plain ASCII.
const COMBINING_MARKS = new RegExp("[\\u0300-\\u036f]", "g");
const stripAccents = (s) =>
  s.normalize ? s.normalize("NFD").replace(COMBINING_MARKS, "") : s;

// "Doe, John A." -> ["john", "a", "doe"]; suffixes and credentials dropped.
// Hyphens and apostrophes become separators, so "Smith-Jones" and "O'Brien"
// contribute their parts as tokens and can match a record holding only one half.
export const nameTokens = (raw) => {
  let s = stripAccents(String(raw || "")).toLowerCase();
  if (!s.trim()) return [];

  // "Last, First Middle" -> "First Middle Last". Only the first comma matters;
  // anything after it ("Doe, John, Jr") is folded back in and suffix-stripped.
  const commaAt = s.indexOf(",");
  if (commaAt > -1) {
    const last = s.slice(0, commaAt);
    const rest = s.slice(commaAt + 1).replace(/,/g, " ");
    s = `${rest} ${last}`;
  }

  return s
    .replace(/[^a-z]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .filter((t) => !NAME_SUFFIXES.has(t));
};

// Exact-match DOB key. Returns "YYYY-MM-DD" or "" when the value can't be read
// confidently. An unreadable DOB must never be treated as equal to anything.
export const normalizeDob = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return "";

  const pad = (n) => String(n).padStart(2, "0");
  const valid = (y, m, d) =>
    y >= 1870 && y <= 2200 && m >= 1 && m <= 12 && d >= 1 && d <= 31;

  // ISO-ish: 1954-03-07, 1954/3/7 (also the value an <input type="date"> gives).
  let m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (m) {
    const [y, mo, d] = [+m[1], +m[2], +m[3]];
    return valid(y, mo, d) ? `${y}-${pad(mo)}-${pad(d)}` : "";
  }

  // US: 3/7/1954, 03-07-54. Two-digit years pivot on the current year: a DOB is
  // always in the past, so "54" is 1954 and "05" is 2005.
  m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2}|\d{4})$/);
  if (m) {
    const mo = +m[1];
    const d = +m[2];
    let y = +m[3];
    if (m[3].length === 2) {
      const currentYy = new Date().getFullYear() % 100;
      y = y <= currentYy ? 2000 + y : 1900 + y;
    }
    return valid(y, mo, d) ? `${y}-${pad(mo)}-${pad(d)}` : "";
  }

  // Day, month name, year: 1-JAN-1954, 01-Jan-54, 1 jan 1954, 1-January-1954.
  // The study enrolled lists (studies_copy/{id}/patients) are typed this way.
  // The month is a word, so day-first is unambiguous here; all-number dates
  // above stay US month/day.
  m = s.match(/^(\d{1,2})[-/ ]([a-z]+)\.?[-/ ](\d{2}|\d{4})$/i);
  if (m) {
    const mo = MONTH_NAMES[m[2].toLowerCase()];
    const d = +m[1];
    let y = +m[3];
    if (!mo) return "";
    if (m[3].length === 2) {
      const currentYy = new Date().getFullYear() % 100;
      y = y <= currentYy ? 2000 + y : 1900 + y;
    }
    return valid(y, mo, d) ? `${y}-${pad(mo)}-${pad(d)}` : "";
  }

  return "";
};

const MONTH_NAMES = (() => {
  const full = [
    "january", "february", "march", "april", "may", "june",
    "july", "august", "september", "october", "november", "december",
  ];
  const map = {};
  full.forEach((name, i) => {
    map[name] = i + 1;
    map[name.slice(0, 3)] = i + 1;
  });
  map.sept = 9;
  return map;
})();

/* -------------------------------------------------------------------------- */
/* Token comparison                                                           */
/* -------------------------------------------------------------------------- */

// Optimal string alignment (Damerau-Levenshtein) distance, abandoned as soon as
// it provably exceeds `max`. Transposition counts as ONE edit, not two, because
// the mistakes this module exists to catch are keyboard slips and "Smtih" for
// "Smith" is the single most common one. Only ever called with max = 1 here, so
// the early exit keeps it cheap across a few thousand candidates.
const editDistanceWithin = (a, b, max) => {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;

  let prevPrev = null;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const row = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let best = Math.min(row[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (
        i > 1 &&
        j > 1 &&
        a[i - 1] === b[j - 2] &&
        a[i - 2] === b[j - 1] &&
        prevPrev[j - 2] + 1 < best
      ) {
        best = prevPrev[j - 2] + 1;
      }
      row[j] = best;
      if (best < rowMin) rowMin = best;
    }
    if (rowMin > max) return max + 1;
    prevPrev = prev;
    prev = row;
  }
  return prev[b.length];
};

const areNicknames = (a, b) => {
  const ga = NICKNAME_INDEX.get(a);
  const gb = NICKNAME_INDEX.get(b);
  if (!ga || !gb) return false;
  for (const g of ga) if (gb.has(g)) return true;
  return false;
};

// How two name tokens relate. Ordered strongest-first; the caller turns this
// into a score and a human-readable reason.
//   "exact"    john    / john
//   "nickname" robert  / bob
//   "initial"  j       / john
//   "typo"     smith   / smtih   (one edit, both >= 4 chars so it stays tight)
//   null       unrelated
export const compareTokens = (a, b) => {
  if (!a || !b) return null;
  if (a === b) return "exact";
  if (a.length === 1 || b.length === 1) {
    return a[0] === b[0] ? "initial" : null;
  }
  if (areNicknames(a, b)) return "nickname";
  if (a.length >= 4 && b.length >= 4 && editDistanceWithin(a, b, 1) <= 1) {
    return "typo";
  }
  return null;
};

const RELATION_SCORE = { exact: 1, nickname: 0.9, typo: 0.85, initial: 0.75 };

/* -------------------------------------------------------------------------- */
/* Name comparison                                                            */
/* -------------------------------------------------------------------------- */

// Compare two full names as *people*. Returns null when they aren't plausibly
// the same person, otherwise { score, reasons }.
//
// The rule: the surnames must relate AND the first names must relate. Middle
// names are optional on both sides, so a record with a middle initial and one
// without still match, which is the common real-world case. Extra middle tokens
// that conflict lower the score but never veto, because "Mary Anne Smith" and
// "Mary Elizabeth Smith" born the same day is exactly the ambiguity a human
// needs to look at.
//
// Partial names count too, at a lower score: a surname entered as just its
// initial ("John S"), or a record carrying only one name at all ("John"). The
// exact-DOB gate is what makes these safe to surface. On its own a shared
// first name proves nothing, but a shared first name AND a shared birthday is
// worth a human look.
export const compareNames = (rawA, rawB) => {
  const a = nameTokens(rawA);
  const b = nameTokens(rawB);
  if (!a.length || !b.length) return null;

  // One side holds a single name, so there is no surname to line up
  // positionally. Fall back to comparing that one token against the other
  // record's first name (then its last, for a record that is a surname only).
  if (a.length === 1 || b.length === 1) return compareSingleToken(a, b);

  const direct = comparePositional(a, b);
  if (direct) return direct;

  // "Smith John" typed where "John Smith" was stored. Only reported when the
  // reversed reading matches on both ends, so it can't fire by coincidence.
  const reversed = comparePositional([...a].reverse(), b);
  if (reversed) {
    return {
      score: Math.min(reversed.score, 0.8),
      reasons: ["First and last name appear reversed", ...reversed.reasons],
    };
  }

  return null;
};

const RELATION_LABEL = {
  exact: "",
  nickname: "nickname",
  typo: "possible typo",
  initial: "initial only",
};

// One record carries a single name token. Scored well below any full-name match
// so it always sorts underneath one, and capped so a partial can never present
// as more certain than it is.
export const ONE_NAME_BOTH_REASON = "Only one name on both records (no last name)";

function compareSingleToken(a, b) {
  const [short, long] = a.length === 1 ? [a, b] : [b, a];
  const token = short[0];
  // A lone initial identifies nobody. "J" would match half the portal.
  if (token.length === 1) return null;

  const firstRel = compareTokens(token, long[0]);
  if (firstRel && firstRel !== "initial") {
    return {
      score: RELATION_SCORE[firstRel] * 0.6,
      reasons: [
        long.length === 1 ? ONE_NAME_BOTH_REASON : "No last name on one record",
        ...(firstRel === "exact" ? [] : [`First name ${RELATION_LABEL[firstRel]}`]),
      ],
    };
  }

  const lastRel = compareTokens(token, long[long.length - 1]);
  if (lastRel && lastRel !== "initial") {
    return {
      score: RELATION_SCORE[lastRel] * 0.55,
      reasons: [
        "Only a last name on one record",
        ...(lastRel === "exact" ? [] : [`Last name ${RELATION_LABEL[lastRel]}`]),
      ],
    };
  }

  return null;
}

// Penalty for a surname known only by its first letter. Kept as a penalty
// rather than a veto: "John S" and "John Smith" sharing a birthday is exactly
// the case a coordinator wants flagged, but it must never outrank a real
// surname match in the list.
const LAST_INITIAL_PENALTY = 0.1;

function comparePositional(a, b) {
  const lastRel = compareTokens(a[a.length - 1], b[b.length - 1]);
  if (!lastRel) return null;

  const firstRel = compareTokens(a[0], b[0]);
  if (!firstRel) return null;

  const reasons = [];
  if (lastRel === "initial") {
    reasons.push("Last name given as an initial only");
  } else if (lastRel !== "exact") {
    reasons.push(`Last name ${RELATION_LABEL[lastRel]}`);
  }
  if (firstRel !== "exact") {
    reasons.push(
      firstRel === "initial"
        ? "First name given as an initial"
        : `First name ${RELATION_LABEL[firstRel]}`
    );
  }

  const middleA = a.slice(1, -1);
  const middleB = b.slice(1, -1);
  let middlePenalty = 0;
  if ((middleA.length === 0) !== (middleB.length === 0)) {
    reasons.push("Middle name/initial present on only one record");
    middlePenalty = 0.05;
  } else if (middleA.length && middleB.length) {
    const related = middleA.some((ma) => middleB.some((mb) => compareTokens(ma, mb)));
    if (!related) {
      reasons.push("Middle names differ");
      middlePenalty = 0.15;
    }
  }

  const score = Math.max(
    0,
    RELATION_SCORE[lastRel] * 0.5 +
      RELATION_SCORE[firstRel] * 0.5 -
      middlePenalty -
      (lastRel === "initial" ? LAST_INITIAL_PENALTY : 0)
  );
  if (!reasons.length) reasons.push("Name matches exactly");
  return { score, reasons };
}

/* -------------------------------------------------------------------------- */
/* Entry point                                                                */
/* -------------------------------------------------------------------------- */

const candidateName = (c) =>
  (c && (c.fullName || c.name || c.patientName)) || "";

/**
 * Find existing patients who look like the person being added.
 *
 * @param {{name:string, dob:string, mrn:string}} entered - the typed values.
 * @param {Array<object>} candidates - existing patient records. Each may carry a
 *   `sourceLabel` describing where it lives ("Consents", "Duplicates", ...);
 *   it is passed through untouched onto the result.
 * @param {{limit?:number, everyRecord?:boolean}} [options] - `everyRecord`: list each
 *   record on its own even when several share an MRN (PFT Uploads shows every copy of
 *   a patient so staff pick which one to merge into). Off by default.
 * @returns {Array<{patient:object, score:number, reasons:string[]}>} sorted
 *   best-match first. Empty when the entered name/DOB are unusable.
 *
 * DOB must match exactly (after normalization). Candidates whose MRN equals the
 * typed MRN are skipped, since that collision is the exact-MRN check's job, and
 * this pass exists precisely for the case where the MRN does NOT line up.
 */
export function findPotentialDuplicates(entered, candidates, options = {}) {
  const { limit = 12, everyRecord = false } = options;
  const dobKey = normalizeDob(entered && entered.dob);
  const tokens = nameTokens(entered && entered.name);
  if (!dobKey || tokens.length < 1) return [];

  const enteredMrn = mrnKey(entered && entered.mrn);
  const seen = new Set();
  const results = [];

  for (const candidate of candidates || []) {
    if (!candidate) continue;
    if (normalizeDob(candidate.dob) !== dobKey) continue;

    // Rule 1 (same MRN) is the caller's exact check, so it's skipped here.
    const candidateMrn = mrnKey(candidate.mrn);
    if (enteredMrn && candidateMrn && candidateMrn === enteredMrn) continue;

    // Rule 2 (same DOB + same person's name) and rule 3 (same DOB + MRN one digit off).
    const nameMatch = compareNames(entered.name, candidateName(candidate));
    const mrnOneOff = mrnOneDigitApart(candidate.mrn, entered && entered.mrn);
    if (!nameMatch && !mrnOneOff) continue;
    const match = {
      score: nameMatch ? nameMatch.score : 0.5,
      reasons: [...(nameMatch ? nameMatch.reasons : []), ...(mrnOneOff ? [MRN_ONE_DIGIT_REASON] : [])],
    };

    // The same person can be listed in more than one store (consents + sheet).
    // Key on MRN when there is one so the dialog shows each record once.
    const key = everyRecord
      ? `rec:${candidate.store || ""}:${candidate.id || candidate.token || candidateName(candidate)}`
      : candidateMrn
        ? `mrn:${candidateMrn}`
        : `id:${candidate.id || candidate.token || candidateName(candidate)}`;
    if (seen.has(key)) continue;
    seen.add(key);

    results.push({ patient: candidate, score: match.score, reasons: match.reasons, nameMatch: nameMatch || null, mrnOneOff });
  }

  results.sort((a, b) => b.score - a.score);
  return results.slice(0, limit);
}
